/** @jsxImportSource @emotion/react */
import { css } from "@emotion/react";

import { memo, useCallback, useEffect, useRef, useState } from "react";

import { useForm, FormProvider } from "react-hook-form";
import {useLocation, useNavigate} from "react-router-dom";

import ReactLoading from 'react-loading';

import NextButton from "components/ui/button/NextButton";
import WhiteButton from "components/ui/button/WhiteButton";

import H3 from "components/parts/title/H3";
import Calendar from "components/parts/calendar/Calendar";
import CautionLargeRed from "components/ui/caution/CautionLargeRed";

// 入力フォームのstore保存
import { useStateMachine } from "little-state-machine";
import { getStore, appointmentAction } from "stores/actions.js";

// CSS読み込み
import { mtgGroup, mtgBody, mtgBodyNoMb, mtgWhiteBackground, validationErrorText} from "assets/css/stylesCss";
import { loadingCss } from "assets/css/shchMtmr";

// JS読み込み
import { formatYubinNo } from "utilities/formater";

// GQL
import useGetLazyQuery from "hooks/useGetLazyQuery";
import { GET_TNTSHOP } from "graphql/query";
import CautionNormalRed from "../components/ui/caution/CautionNormalRed";

/**
 * ご希望日時の選択
 */
const AppointmentCalendar = memo(() => {

  // 画面遷移用
  const navigate = useNavigate();
  const location = useLocation();

  // サービス対象エリア
  const [isOutOfServiceArea, setIsOutOfServiceArea] = useState(false);
  const [tntShopInfo, setTntShopInfo] = useState({tntHjnCd:null, tntShopCd: null, tntShopName: null, tntShopTelNo: null});

  // 住所コードから担当店舗を取得するフック
  const [getTntShopInfo, loading] = useGetLazyQuery(GET_TNTSHOP);

  // store取得・更新
  const { state } = useStateMachine(getStore);
  const { actions } = useStateMachine({ appointmentAction });

  // 余力なし判定
  const [isCapacity, setNoCapacity] = useState(false);

  const methods = useForm({
    defaultValues: {
      isActive: false,
      yubinNo: state.storeAppointment.yubinNo,
      addressKj: state.storeAppointment.addressKj,
      addressKjLabel: state.storeAppointment.addressKjLabel,
      appointmentYmd: state.storeAppointment.appointmentYmd,
      appointmentHmTimeCd: state.storeAppointment.appointmentHmTimeCd,
    },
    mode: "onSubmit",
    reValidateMode: "onSubmit",
  });

  const {
    formState: { isValid },
  } = methods;

  // カレンダー情報
  const [kbuHmDayInfo, setKbuHmDayInfo] = useState({
    appointmentYmd: state.storeAppointment.appointmentYmd,
    appointmentHmTimeCd: state.storeAppointment.appointmentHmTimeCd,
    selectedPage: state.storeAppointment.selectedPage,
  });

  // 初期表示・再描画時
  const refFirstRef = useRef(true);
  useEffect(() => {

    if (process.env.NODE_ENV === "development") {
      if (refFirstRef.current) {
        refFirstRef.current = false;
        return;
      }
    }
    (async () => {
        // 住所情報を生成
        await getTntShop();
    })()

    // 余力なしエラーの場合初期化
    if (location.state?.noCapacity) {
      setNoCapacity(true);

      // カレンダーの選択状態を初期化
      setKbuHmDayInfo({
        appointmentYmd: "",        // yyyyMMdd
        appointmentHmTimeCd: "",   // hh00hh00(例：13001500)
        selectedPage: 0,
      });

    }

  }, []);

  // 住所コードから担当店舗を取得する
  const getTntShop = useCallback(async () => {

    const addressKj = state.storeAppointment.addressKj;
    //　住所コードが正常に入力されていない場合、郵便番号選択画面へ遷移
    if(!addressKj) navigate("/");

    // 住所コードから担当店舗を取得する
    const param = { input: { addressCd: addressKj}};
    await getTntShopInfo(param,
      (data) => {
          // エラー存在チェック
          let err = data.errorResponse;
          if(!err) {
            // 担当情報を設定
            setTntShopInfo({
              tntHjnCd:data.tntHjnCd,
              tntShopCd: data.tntShopCd,
              tntShopName: data.tntShopName,
              tntShopTelNo: data.tntShopTelNo
            })
          } else {
            // サービス対象エリア外を設定
            setIsOutOfServiceArea(true);
          }
      },
      // エラー発生時
      (e) => {
        console.log("失敗…" + e)
        navigate("/error", {
          state: { errorCode: 500 },
        });
      }
    );
  }, []);

  const onSubmit = (data) => {
    data.selectedPage = kbuHmDayInfo.selectedPage;
    data.tntHjnCd = tntShopInfo.tntHjnCd;
    data.tntShopCd = tntShopInfo.tntShopCd;
    data.tntShopName = tntShopInfo.tntShopName;
    data.tntTelNo = tntShopInfo.tntShopTelNo;
    actions.appointmentAction(data);
    navigate("/kikiSelect", {
      state: { isSubmit: true },
    });
  };

  // 訪問エリアを修正する
  const handleOnClickAppointment = () => {
    navigate("/appointment");
  };

  return (
    <>
      {!loading && (
        <>
          <h1>ご希望日時の選択</h1>
          <FormProvider {...methods}>

            {/* お申込み条件確認 */}

            {/** 訪問エリア */}
            <div css={mtgGroup}>
              <H3 title="訪問エリア" required={true}></H3>
              <div css={mtgWhiteBackground}>
                <p css={mtgBodyNoMb}>
                  {/** 郵便番号 */}
                  {formatYubinNo(state.storeAppointment.yubinNo, true)}
                  <br />
                  {/** 住所 */}
                  {state.storeAppointment.addressKjLabel}
                </p>
              </div>
            </div>

            {/** 訪問エリア修正ボタン */}
            <div css={mtgGroup}>
              <WhiteButton
                value="訪問エリアを修正する"
                isActive={true}
                fn={handleOnClickAppointment}
              >
                訪問エリアを修正する
              </WhiteButton>
            </div>

            {/** 訪問ご希望日時 */}
            {!isOutOfServiceArea ? (
                <div css={mtgGroup}>
                <H3 title="訪問ご希望日時" required={true}></H3>
                {/** MEMO:通常のmtgBodyとmarginBottomが違うため直書き */}
                <p css={mtgBody} style={{ marginBottom: '12px' }}>訪問ご希望日時を選択してください。</p>
                <Calendar
                  kbuHmDayInfo={kbuHmDayInfo}
                  setKbuHmDayInfo={setKbuHmDayInfo}
                  tntShopTelNo={tntShopInfo.tntShopTelNo}
                  addressKj={state.storeAppointment.addressKj}
                  setIsActive={setNoCapacity}
                  setIsOutOfServiceArea={setIsOutOfServiceArea}
                  setNoCapacity ={setNoCapacity}
                />
                  {isCapacity && (
                      <p css={validationErrorText}>
                        ご希望いただいた日時での予約をお受けすることができませんでした。恐れ入りますが再度ご入力ください。
                      </p>
                  )}
                </div>
              )
              :
              (
                <div css={mtgGroup}>
                  <CautionLargeRed>出張見積エリア外</CautionLargeRed>
                  <p css={mtgBody}>
                    誠に申し訳ございませんが、ご入力いただいたご住所は本サービスの対象区域外のためご依頼を承ることができません。
                  </p>
                </div>
              )
            }

            {/** 次へ */}
            <form onSubmit={methods.handleSubmit(onSubmit)}>
              <NextButton
                isActive={isValid}
                value="次へ"
                isInitialState={false}
                isInvalidDate = {isCapacity}
                isOutOfServiceArea={isOutOfServiceArea}
              ></NextButton>
            </form>
          </FormProvider>
        </>
      )}

      {/** ローディング */}
      {loading && (
        <div css={[loadingCss]}>
          <ReactLoading type="spin" color="#b5b5b5" height="50px" width="50px" />
        </div>
      )}
    </>
  );
});

export default AppointmentCalendar;
